<script setup lang="ts">
import { ref, nextTick } from 'vue';
import {
    BTextField, BBtn, BIcon,
} from '@/design-system';
import { useTranslation } from '@/composables';

const props = withDefaults(
    defineProps<{
        modelValue: string;
        placeholder?: string;
        disabled?: boolean;
        loading?: boolean;
    }>(),
    {
        placeholder: 'chatbot.inputPlaceholder',
        disabled: false,
        loading: false,
    },
);

const emit = defineEmits<{
    'update:modelValue': [value: string];
    send: [message: string];
}>();

const { t } = useTranslation();

const textFieldRef = ref<typeof BTextField | null>(null);

const handleInput = (value: string | number): void => {
    emit('update:modelValue', String(value));
};

const handleSend = async (): Promise<void> => {
    const message = props.modelValue.trim();
    if (message && !props.disabled && !props.loading) {
        emit('send', message);
        await nextTick();
        const inputElement = textFieldRef.value?.$el?.querySelector('input');
        if (inputElement && typeof inputElement.focus === 'function') {
            inputElement.focus();
        }
    }
};

const handleKeydown = (event: KeyboardEvent): void => {
    if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        handleSend();
    }
};
</script>

<template>
    <div class="chat-input-wrapper d-flex flex-column ga-1">
        <div class="chat-input d-flex align-center ga-1 px-1">
            <BTextField
                ref="textFieldRef"
                :model-value="modelValue"
                :placeholder="placeholder"
                :disabled="disabled || loading"
                variant="solo"
                autofocus
                :flat="true"
                density="compact"
                hide-details
                class="flex-grow-1"
                @update:model-value="handleInput"
                @keydown="handleKeydown"
            >
            </BTextField>
            <BBtn
                class="send-btn"
                density="comfortable"
                icon
                color="grey-700"
                :disabled="disabled || loading"
                @click="handleSend"
            >
                <BIcon color="white">mdi-arrow-up</BIcon>
            </BBtn>
        </div>
        <div class="powered-by-ai text-center">
            {{ t('chatbot.poweredByAi') }}
        </div>
    </div>
</template>

<style scoped lang="scss">
.chat-input {
    background: white;
    border: 1px solid rgba(var(--v-theme-grey-300));
    border-radius: 30px;
    box-shadow:
        0 0 15px 3px #dad7e45e,
        0 0 transparent,
        0 0 transparent;
    height: 60px;

    :deep(.v-field) {
        background: transparent;
        border-radius: 24px;
    }

    :deep(.v-field__input) {
        font-size: 0.8em;
    }
}

.send-btn {
    border-radius: 50%;
    width: 40px;
    height: 40px;
    margin-right: 4px;
}

.powered-by-ai {
    font-size: 0.7rem;
    color: rgba(var(--v-theme-grey-500));
}
</style>
